import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  createDataContext,
  IndexedDbLocalStore,
  lastSyncAt,
  type DataContext as DataCtx,
} from '@life-os/data';
import {
  createFoundationRegistry,
  type FeatureRegistry,
  type Platform,
  type PlatformAvailability,
  type RuntimeStatus,
} from '@life-os/app-core';
import { useAutoSync } from '../lib/sync.js';

interface Store {
  readonly data: DataCtx;
  readonly registry: FeatureRegistry;
  /** Increments on every mutation so screens can re-query. */
  readonly revision: number;
  /** Bump the revision to trigger dependent screens to refresh. */
  readonly refresh: () => void;
  getRuntimeStatus(): Promise<RuntimeStatus>;
}

const StoreContext = createContext<Store | null>(null);

const WEB_PLATFORMS: Record<Platform, PlatformAvailability> = {
  web: 'available',
  linux: 'planned',
  windows: 'planned',
  android: 'planned',
  ios: 'planned',
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const data = useMemo(
    () => createDataContext({ ownerId: 'nikhil', store: new IndexedDbLocalStore() }),
    [],
  );
  const registry = useMemo(() => createFoundationRegistry(), []);
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision((r) => r + 1), []);

  // Background sync keeps every device current (no-op when no cloud configured).
  useAutoSync(data.store, revision, refresh);

  const getRuntimeStatus = useCallback(async (): Promise<RuntimeStatus> => {
    const pending = await data.queue.pending();
    const conflicts = await data.queue.conflicts();
    return {
      localDb: 'ready',
      pendingMutations: pending.length,
      conflicts: conflicts.length,
      lastSyncAt: await lastSyncAt(data.store),
      platformCapabilities: WEB_PLATFORMS,
    };
  }, [data]);

  const value = useMemo<Store>(
    () => ({ data, registry, revision, refresh, getRuntimeStatus }),
    [data, registry, revision, refresh, getRuntimeStatus],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}

export function useServices() {
  return useStore().data.services;
}

export function useRegistry() {
  return useStore().registry;
}
